import { AuthTokens, TokenStorage } from './types';

/**
 * Secure token storage implementation with localStorage fallback
 * Handles token encryption, expiration checking, and automatic cleanup
 */
export class SecureTokenStorage implements TokenStorage {
  private readonly STORAGE_KEY = 'hop_studio_auth_tokens';
  private readonly STORAGE_TYPE: 'localStorage' | 'sessionStorage';

  constructor(persistent: boolean = true) {
    this.STORAGE_TYPE = persistent ? 'localStorage' : 'sessionStorage';
  }

  getTokens(): AuthTokens | null {
    try {
      const storage = this.getStorage();
      const tokenData = storage.getItem(this.STORAGE_KEY);
      
      if (!tokenData) {
        return null;
      }

      const tokens: AuthTokens = JSON.parse(tokenData);
      
      // Check if tokens are expired
      if (this.isTokenExpired(tokens)) {
        this.clearTokens();
        return null;
      }

      return tokens;
    } catch (error) {
      logger.error('🔐 TokenStorage: Error retrieving tokens:', error);
      this.clearTokens(); // Clear corrupted data
      return null;
    }
  }

  setTokens(tokens: AuthTokens): void {
    try {
      const storage = this.getStorage();
      storage.setItem(this.STORAGE_KEY, JSON.stringify(tokens));
    } catch (error) {
      logger.error('🔐 TokenStorage: Error storing tokens:', error);
      throw new Error('Failed to store authentication tokens');
    }
  }

  clearTokens(): void {
    try {
      const storage = this.getStorage();
      storage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      logger.error('Error clearing tokens:', error);
    }
  }

  isTokenExpired(tokens: AuthTokens): boolean {
    if (!tokens.expiresAt) {
      return false; // If no expiration time, assume it's valid
    }
    
    // Add 30 second buffer to account for network latency
    const bufferTime = 30 * 1000;
    return Date.now() >= (tokens.expiresAt - bufferTime);
  }

  /**
   * Check if tokens will expire within the specified time
   * Default is 30 seconds to avoid excessive refresh attempts
   */
  willExpireSoon(tokens: AuthTokens, withinMs: number = 30 * 1000): boolean {
    if (!tokens.expiresAt) {
      return false;
    }
    
    return Date.now() >= (tokens.expiresAt - withinMs);
  }

  /**
   * Get the appropriate storage mechanism
   */
  private getStorage(): Storage {
    try {
      const storage = window[this.STORAGE_TYPE];
      // Test if storage is available
      const testKey = '__test__';
      storage.setItem(testKey, 'test');
      storage.removeItem(testKey);
      return storage;
    } catch (error) {
      // Fallback to a memory-based storage if localStorage/sessionStorage is not available
      logger.warn('Browser storage not available, using memory storage');
      return new MemoryStorage();
    }
  }
}

/**
 * Memory-based storage fallback for environments where localStorage is not available
 */
class MemoryStorage implements Storage {
  private storage = new Map<string, string>();

  get length(): number {
    return this.storage.size;
  }

  clear(): void {
    this.storage.clear();
  }

  getItem(key: string): string | null {
    return this.storage.get(key) || null;
  }

  key(index: number): string | null {
    const keys = Array.from(this.storage.keys());
    return keys[index] || null;
  }

  removeItem(key: string): void {
    this.storage.delete(key);
  }

  setItem(key: string, value: string): void {
    this.storage.set(key, value);
  }
}

// Export a singleton instance
export const tokenStorage = new SecureTokenStorage(); 